import { useCallback, useState } from 'react';
import useStore from '../../store/useStore';
import { dashboardsAPI, savedReportsAPI } from '../../services/api';
import { loadDashboardData } from '../../utils/loadDashboardData';
import { defaultSize, findFreeSlot, widgetLabel, kpiTileConfig, kpiTilePosition, KPI_TILE } from './editorUtils';

/**
 * Everything the on-dashboard editor does. Each action saves straight away
 * and refreshes the dashboard in the store, so there is no separate "save"
 * step for widgets; layout moves are saved by the grid as they happen.
 *
 * @param {() => {x,y,w,h}[]} getLayoutItems current grid items (for finding free space)
 */
export default function useDashboardEditor(getLayoutItems) {
  const dashboard = useStore((state) => state.currentDashboard);
  const companyId = useStore((state) => state.currentCompany?.id);
  const [panel, setPanel] = useState(null); // { type: 'library' } | { type: 'widget', widgetId } | { type: 'dashboard' }
  const [busyKey, setBusyKey] = useState(null);
  const [lastAddedKey, setLastAddedKey] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { text, tone }
  const [reportBuilder, setReportBuilder] = useState(null); // { widget? }
  const [definition, setDefinition] = useState(null); // metric definition being edited

  const say = useCallback((text, tone = 'ok') => {
    setNotice({ text, tone });
    window.clearTimeout(say.timer);
    say.timer = window.setTimeout(() => setNotice(null), tone === 'error' ? 6000 : 3000);
  }, []);

  const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  /** Re-read the dashboard; returns ids that were not there before. */
  const reload = useCallback(async () => {
    const id = useStore.getState().currentDashboard?.id;
    if (!id) return [];
    const before = new Set((useStore.getState().currentDashboard?.widgets || []).map((w) => w.id));
    const res = await dashboardsAPI.getById(id);
    if (!res?.success) return [];
    const fresh = res.data;
    useStore.setState((state) => ({
      currentDashboard: fresh,
      dashboards: state.dashboards.map((d) => (d.id === fresh.id
        ? { ...d, name: fresh.name, is_default: fresh.is_default, default_date_range: fresh.default_date_range, stale_threshold_minutes: fresh.stale_threshold_minutes }
        : (fresh.is_default ? { ...d, is_default: false } : d))),
    }));
    return (fresh.widgets || []).filter((w) => !before.has(w.id)).map((w) => w.id);
  }, []);

  const flash = (ids) => {
    if (!ids.length) return;
    setHighlightId(ids[0]);
    window.setTimeout(() => setHighlightId((cur) => (cur === ids[0] ? null : cur)), 2500);
  };

  const slotFor = (kind, widgetType) => {
    const { w, h } = defaultSize(kind, widgetType);
    return findFreeSlot(getLayoutItems(), w, h, 12);
  };

  /** Add something picked in the widget library. */
  const add = useCallback(async (action, key) => {
    if (!dashboard) return;
    setBusyKey(key);
    try {
      let res;
      let title;
      if (action.kind === 'metric') {
        title = action.metric.display_name || action.metric.metric_key;
        res = await dashboardsAPI.addWidget(dashboard.id, action.metric.metric_key, slotFor('metric', action.metric.widget_type), {});
      } else if (action.kind === 'report') {
        title = action.report.name;
        res = await dashboardsAPI.addReportWidget(dashboard.id, action.report.id, slotFor('dataset'), {});
      } else if (action.kind === 'statement') {
        title = action.layout.title;
        res = await dashboardsAPI.addStatementWidget(dashboard.id, action.layout.datasetName, action.layout.key, slotFor('statement'));
      } else if (action.kind === 'kpi') {
        title = action.kpi.name;
        res = await dashboardsAPI.addKpiWidget(dashboard.id, kpiTilePosition(slotFor('kpi', KPI_TILE)), kpiTileConfig(action.kpi));
      } else if (action.kind === 'library') {
        const item = action.item;
        title = item.title;
        const created = await savedReportsAPI.create(companyId, {
          name: item.title, cubeQuery: item.cubeQuery, display: { displayType: item.displayType, inheritDateRange: true }, refreshMinutes: item.refreshMinutes || 30, visibility: 'company',
        });
        if (!created?.success) throw new Error(created?.error || 'Could not save the report');
        res = await dashboardsAPI.addReportWidget(dashboard.id, created.data.id, slotFor('dataset'), {});
      }
      if (!res?.success) throw new Error(res?.error || 'Could not add the widget');
      flash(await reload());
      setLastAddedKey(key);
      say(`Added “${title}”`);
    } catch (err) {
      say(errorText(err, 'Could not add the widget'), 'error');
    } finally {
      setBusyKey(null);
    }
  }, [dashboard, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveWidgetConfig = useCallback(async (widget, config) => {
    setSaving(true);
    try {
      const res = await dashboardsAPI.updateWidget(dashboard.id, widget.id, { config });
      if (!res?.success) throw new Error(res?.error || 'Could not save');
      await reload();
      say('Widget saved');
      setPanel(null);
    } catch (err) {
      say(errorText(err, 'Could not save the widget'), 'error');
    } finally {
      setSaving(false);
    }
  }, [dashboard, reload, say]);

  const remove = useCallback(async (widget) => {
    try {
      const res = await dashboardsAPI.deleteWidget(dashboard.id, widget.id);
      if (!res?.success) throw new Error(res?.error || 'Could not remove');
      setPanel((p) => (p?.type === 'widget' && p.widgetId === widget.id ? null : p));
      await reload();
      say(`Removed “${widgetLabel(widget)}”`);
    } catch (err) {
      say(errorText(err, 'Could not remove the widget'), 'error');
    }
  }, [dashboard, reload, say]);

  const duplicate = useCallback(async (widget) => {
    const kind = widget.widget_kind || 'metric';
    const current = getLayoutItems().find((i) => i.i === String(widget.id));
    const size = current ? { w: current.w, h: current.h } : defaultSize(kind, widget.widget_type);
    const pos = findFreeSlot(getLayoutItems(), size.w, size.h, 12);
    const config = widget.config || {};
    try {
      let res;
      if (kind === 'metric') res = await dashboardsAPI.addWidget(dashboard.id, widget.metric_key, pos, config);
      else if (kind === 'kpi') res = await dashboardsAPI.addKpiWidget(dashboard.id, pos, config);
      else if (kind === 'statement') res = await dashboardsAPI.addStatementWidget(dashboard.id, config.datasetName || widget.dataset_name, config.layoutKey, pos, config);
      else if (widget.report) res = await dashboardsAPI.addReportWidget(dashboard.id, widget.report.id, pos, config);
      else res = await dashboardsAPI.addDatasetWidget(dashboard.id, widget.dataset_name, pos, config);
      if (!res?.success) throw new Error(res?.error || 'Could not duplicate');
      const added = await reload();
      flash(added);
      if (added[0]) setPanel({ type: 'widget', widgetId: added[0] });
      say(`Duplicated “${widgetLabel(widget)}”`);
    } catch (err) {
      say(errorText(err, 'Could not duplicate the widget'), 'error');
    }
  }, [dashboard, reload, say]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Report builder: create a report and put it on the dashboard. */
  const createReport = useCallback(async (report) => {
    const created = await savedReportsAPI.create(companyId, report);
    if (!created?.success) throw new Error(created?.error || 'Could not save the report');
    const res = await dashboardsAPI.addReportWidget(dashboard.id, created.data.id, slotFor('dataset'), {});
    if (!res?.success) throw new Error(res?.error || 'Could not add the report');
    flash(await reload());
    setReportBuilder(null);
    say(`Added “${report.name}”`);
  }, [companyId, dashboard, reload, say]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Report builder: save changes to a widget's report (older widgets become saved reports). */
  const updateReport = useCallback(async (widget, report) => {
    if (widget.report) {
      const res = await savedReportsAPI.update(widget.report.id, companyId, report);
      if (!res?.success) throw new Error(res?.error || 'Could not update the report');
    } else {
      const created = await savedReportsAPI.create(companyId, report);
      if (!created?.success) throw new Error(created?.error || 'Could not save the report');
      const current = getLayoutItems().find((i) => i.i === String(widget.id));
      const pos = current ? { x: current.x, y: current.y, w: current.w, h: current.h } : slotFor('dataset');
      await dashboardsAPI.addReportWidget(dashboard.id, created.data.id, pos, { title: widget.config?.title });
      await dashboardsAPI.deleteWidget(dashboard.id, widget.id);
      setPanel(null);
    }
    await reload();
    setReportBuilder(null);
    say(`Saved “${report.name}”`);
  }, [companyId, dashboard, reload, say]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDashboard = useCallback(async (fields) => {
    setSaving(true);
    try {
      const res = await dashboardsAPI.update(dashboard.id, fields);
      if (!res?.success) throw new Error(res?.error || 'Could not save');
      await reload();
      if (fields.defaultDateRange !== undefined) useStore.getState().setDashboardDateRange(fields.defaultDateRange || null);
      say('Dashboard settings saved');
      setPanel(null);
    } catch (err) {
      say(errorText(err, 'Could not save the dashboard'), 'error');
    } finally {
      setSaving(false);
    }
  }, [dashboard, reload, say]);

  const deleteDashboard = useCallback(async () => {
    try {
      const name = dashboard.name;
      const res = await dashboardsAPI.delete(dashboard.id);
      if (!res?.success) throw new Error(res?.error || 'Could not delete');
      setPanel(null);
      useStore.getState().setEditMode(false);
      await loadDashboardData({ companyId });
      say(`Deleted “${name}”`);
    } catch (err) {
      say(errorText(err, 'Could not delete the dashboard'), 'error');
    }
  }, [dashboard, companyId, say]);

  return {
    panel, setPanel, busyKey, lastAddedKey, highlightId, saving, notice, say,
    reportBuilder, setReportBuilder, definition, setDefinition,
    add, saveWidgetConfig, remove, duplicate, createReport, updateReport, saveDashboard, deleteDashboard, reload,
  };
}
